/**
 * ComparisonPanel — second set of filters for inline comparison.
 *
 * Renders filter controls (geo, device, dates, alignment, comparison mode)
 * inside a visually distinct MUI Paper, plus a "Load Comparison" button.
 * Table rendering is handled by SPBReportContent via split-cell display.
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { SelectChangeEvent } from "@mui/material/Select";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import type { SecondTableFilters } from "../../types";
import type { AlignmentType, ComparisonModeType } from "../../utils/dateUtils";
import { alignDates, computeBaselineDates } from "../../utils/dateUtils";
import {
  fetchMarketplaces,
  fetchRegions,
} from "../../services/dashboardService";
import type { Region } from "../../utils/marketplaceUtils";

interface GeoGroup {
  label: string;
  regionCode: string;
  marketplaces: { id: string; label: string }[];
}

interface ComparisonPanelProps {
  enabled: boolean;
  filters: SecondTableFilters;
  onFiltersChange: (filters: SecondTableFilters) => void;
  onLoadComparison: () => void;
  isLoading: boolean;
}

const ComparisonPanel: React.FC<ComparisonPanelProps> = ({
  enabled,
  filters,
  onFiltersChange,
  onLoadComparison,
  isLoading,
}) => {
  const theme = useTheme();
  const [geoGroups, setGeoGroups] = useState<GeoGroup[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [, regData] = await Promise.all([
          fetchMarketplaces(),
          fetchRegions(),
        ]);
        if (cancelled) return;
        const regionOrder = ["na", "eu", "eu5", "row"];
        const regionLabels: Record<string, string> = {
          na: "North America",
          eu: "Europe",
          eu5: "EU5",
          row: "Rest-of-World",
        };
        const groups: GeoGroup[] = [];
        for (const code of regionOrder) {
          const region = regData.find((r: Region) => r.region_code === code);
          if (!region) continue;
          let parsed: any = region.marketplaces;
          while (typeof parsed === "string") {
            try {
              parsed = JSON.parse(parsed);
            } catch {
              parsed = [];
              break;
            }
          }
          const mktList = Array.isArray(parsed) ? parsed : [];
          groups.push({
            label: regionLabels[code] ?? region.display_name,
            regionCode: code,
            marketplaces: mktList.map((m: any) => ({
              id: String(m.marketplace_id),
              label: `${m.marketplace_id} (${m.country_code})`,
            })),
          });
        }
        setGeoGroups(groups);
      } catch (err) {
        console.warn("Failed to fetch geo data:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const update = useCallback(
    (partial: Partial<SecondTableFilters>) => {
      onFiltersChange({ ...filters, ...partial });
    },
    [filters, onFiltersChange]
  );

  const handleAlignment = useCallback(
    (_: React.MouseEvent<HTMLElement>, value: AlignmentType | null) => {
      if (!value) return;
      const aligned = alignDates(
        filters.targetStartDate,
        filters.targetEndDate,
        value
      );
      const baseline = computeBaselineDates(
        aligned.start,
        aligned.end,
        filters.comparisonMode
      );
      update({
        alignment: value,
        targetStartDate: aligned.start,
        targetEndDate: aligned.end,
        baselineStartDate: baseline.start,
        baselineEndDate: baseline.end,
      });
    },
    [filters, update]
  );

  const handleComparisonMode = useCallback(
    (_: React.MouseEvent<HTMLElement>, value: ComparisonModeType | null) => {
      if (!value) return;
      const baseline = computeBaselineDates(
        filters.targetStartDate,
        filters.targetEndDate,
        value
      );
      update({
        comparisonMode: value,
        baselineStartDate: baseline.start,
        baselineEndDate: baseline.end,
      });
    },
    [filters, update]
  );

  if (!enabled) return null;

  const isCustom = filters.comparisonMode === "Custom";
  const tgSx = {
    "& .MuiToggleButton-root": {
      px: 1,
      py: 0.3,
      textTransform: "none",
      fontSize: "0.75rem",
      lineHeight: 1.4,
    },
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: 2,
        borderColor: "warning.light",
        borderRadius: 2,
        bgcolor:
          theme.palette.mode === "dark"
            ? "rgba(255,183,77,0.04)"
            : "rgba(255,152,0,0.03)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1.5,
        }}
      >
        <CompareArrowsIcon sx={{ color: "warning.main", fontSize: "1.2rem" }} />
        <Typography variant="subtitle2" fontWeight={600}>
          Comparison Filters
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "flex-end",
        }}
      >
        {/* Geo */}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="cmp-geo-label">Geo</InputLabel>
          <Select
            labelId="cmp-geo-label"
            value={filters.geo}
            label="Geo"
            onChange={(e: SelectChangeEvent) => update({ geo: e.target.value })}
          >
            <MenuItem value="ww">Worldwide</MenuItem>
            {geoGroups.map((group) => [
              <ListSubheader key={`hdr-${group.regionCode}`}>
                {group.label}
              </ListSubheader>,
              <MenuItem
                key={group.regionCode}
                value={group.regionCode}
                sx={{ pl: 3 }}
              >
                {group.label} (aggregate)
              </MenuItem>,
              ...group.marketplaces.map((m) => (
                <MenuItem key={m.id} value={m.id} sx={{ pl: 5 }}>
                  {m.label}
                </MenuItem>
              )),
            ])}
          </Select>
        </FormControl>

        {/* Device */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="cmp-device-label">Device</InputLabel>
          <Select
            labelId="cmp-device-label"
            value={filters.device}
            label="Device"
            onChange={(e: SelectChangeEvent) =>
              update({ device: e.target.value })
            }
          >
            <MenuItem value="Desktop-Mobile">Desktop+Mobile</MenuItem>
            <MenuItem value="Desktop">Desktop</MenuItem>
            <MenuItem value="Mobile">Mobile</MenuItem>
          </Select>
        </FormControl>

        {/* Target dates + alignment */}
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              sx={{ whiteSpace: "nowrap" }}
            >
              Target
            </Typography>
            <ToggleButtonGroup
              value={filters.alignment}
              exclusive
              onChange={handleAlignment}
              size="small"
              sx={tgSx}
            >
              {(["D", "W", "M", "Q", "H", "Y"] as AlignmentType[]).map((a) => (
                <ToggleButton key={a} value={a}>
                  {a}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <TextField
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={filters.targetStartDate}
              sx={{ width: 145 }}
              onChange={(e) => {
                const aligned = alignDates(
                  e.target.value,
                  filters.targetEndDate,
                  filters.alignment
                );
                const baseline = computeBaselineDates(
                  aligned.start,
                  aligned.end,
                  filters.comparisonMode
                );
                update({
                  targetStartDate: aligned.start,
                  targetEndDate: aligned.end,
                  baselineStartDate: baseline.start,
                  baselineEndDate: baseline.end,
                });
              }}
            />
            <Typography variant="caption" color="text.secondary">
              to
            </Typography>
            <TextField
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={filters.targetEndDate}
              sx={{ width: 145 }}
              onChange={(e) => {
                const baseline = computeBaselineDates(
                  filters.targetStartDate,
                  e.target.value,
                  filters.comparisonMode
                );
                update({
                  targetEndDate: e.target.value,
                  baselineStartDate: baseline.start,
                  baselineEndDate: baseline.end,
                });
              }}
            />
          </Stack>
        </Stack>

        {/* Comparison mode + baseline dates */}
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              sx={{ whiteSpace: "nowrap" }}
            >
              Compare
            </Typography>
            <ToggleButtonGroup
              value={filters.comparisonMode}
              exclusive
              onChange={handleComparisonMode}
              size="small"
              sx={tgSx}
            >
              {(
                [
                  "YoY",
                  "HoH",
                  "QoQ",
                  "MoM",
                  "WoW",
                  "DoD",
                  "Custom",
                ] as ComparisonModeType[]
              ).map((m) => (
                <ToggleButton key={m} value={m}>
                  {m}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <TextField
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={filters.baselineStartDate}
              disabled={!isCustom}
              sx={{ width: 145 }}
              onChange={(e) => update({ baselineStartDate: e.target.value })}
            />
            <Typography variant="caption" color="text.secondary">
              to
            </Typography>
            <TextField
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={filters.baselineEndDate}
              disabled={!isCustom}
              sx={{ width: 145 }}
              onChange={(e) => update({ baselineEndDate: e.target.value })}
            />
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
};

export default ComparisonPanel;
